import { Building2, ChevronRight, RotateCcw, TrainTrack } from "lucide-react";
import { useMemo } from "react";
import Flag from "react-world-flags";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { ColocaPopover } from "@/shared/components/overlays/ColocaPopover";
import { ColocaTooltip } from "@/shared/components/overlays/ColocaTooltip";
import useTranslations from "@/shared/lang/useTranslations";
import { formatRail } from "@/shared/utils/railDisplay";

import { RAIL_COUNTRY } from "../../constants/paymentRails.constants";
import type { RailAvailabilityCell } from "../../types/paymentRails.types";
import { formatSince, hasOwnOverride, scopedState } from "./railCellDisplay";

export interface RailAvailabilityTableProps {
  /** Cells of ONE direction — the page renders a table per section (payins / payouts). */
  cells: RailAvailabilityCell[];
  isLoading: boolean;
  /** Rows the loading skeleton paints — the section's cell count, so the layout doesn't jump. */
  skeletonRows: number;
  /** Global view (null) or the organization whose effective state the rows show. */
  scopeOrgId: string | null;
  orgNameById: Map<string, string>;
  /** True while a write is in flight — the switches lock so a double flip cannot race. */
  isMutating: boolean;
  onToggle: (cell: RailAvailabilityCell, nextEnabled: boolean) => void;
  onClearOverride: (cell: RailAvailabilityCell) => void;
  /** Jumps the page scope to an organization — the overrides list is a shortcut into its view. */
  onViewOrg: (organizationId: string) => void;
}

/**
 * One row per rail of a single direction. The status column shows the EFFECTIVE verdict for the current scope
 * (with the operator's reason inline — it is internal-only, so showing it costs nothing); the source column
 * says which row produced it (default / global / the organization's own override), because with
 * most-specific-wins the same pill can come from three different places — and undoing it differs in each.
 */
export function RailAvailabilityTable({
  cells,
  isLoading,
  skeletonRows,
  scopeOrgId,
  orgNameById,
  isMutating,
  onToggle,
  onClearOverride,
  onViewOrg,
}: RailAvailabilityTableProps) {
  const { t, activeLanguage } = useTranslations("paymentRails");

  const columns = useMemo<ColocaDataTableColumn<RailAvailabilityCell>[]>(() => {
    const base: ColocaDataTableColumn<RailAvailabilityCell>[] = [
      {
        id: "rail",
        header: t("th_rail"),
        cell: (cell) => (
          <ColocaBadge variant="secondary" className="font-sans">
            {formatRail(cell.rail)}
          </ColocaBadge>
        ),
      },
      {
        id: "country",
        header: t("th_country"),
        cell: (cell) => {
          const country = RAIL_COUNTRY[cell.rail];
          if (!country) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="flex items-center gap-2">
              <Flag code={country.iso2} className="h-4 w-6 shrink-0 rounded object-cover" />
              <span>{t(country.labelKey)}</span>
            </span>
          );
        },
      },
      {
        id: "status",
        header: t("th_status"),
        cell: (cell) => {
          const state = scopedState(cell, scopeOrgId);
          const pill = (
            <ColocaStatusPill variant={cell.effective_enabled ? "success" : "danger"} size="sm" withDot>
              {t(cell.effective_enabled ? "status_enabled" : "status_disabled")}
            </ColocaStatusPill>
          );
          if (!state?.reason) return pill;
          return (
            <div className="space-y-1">
              {pill}
              <ColocaTooltip
                triggerRender={
                  <p className="max-w-56 cursor-help truncate text-xs text-muted-foreground">
                    {state.reason}
                  </p>
                }
              >
                {state.reason}
              </ColocaTooltip>
            </div>
          );
        },
      },
      {
        id: "source",
        header: t("th_source"),
        cell: (cell) => {
          const state = scopedState(cell, scopeOrgId);
          if (!state) return <span className="text-xs text-muted-foreground">{t("source_default")}</span>;
          const sourceKey = hasOwnOverride(cell, scopeOrgId)
            ? "source_org_override"
            : scopeOrgId
              ? "source_global"
              : "source_global_setting";
          return (
            <span className="text-xs text-muted-foreground">
              {t(sourceKey)} · {formatSince(state.since, activeLanguage)}
            </span>
          );
        },
      },
    ];

    if (!scopeOrgId) {
      base.push({
        id: "overrides",
        header: t("th_overrides"),
        cell: (cell) => {
          if (cell.org_overrides.length === 0) {
            return <span className="text-muted-foreground">—</span>;
          }
          return (
            <ColocaPopover
              align="start"
              contentClassName="w-80"
              description={t("overrides_popover_hint")}
              trigger={
                <ColocaButton variant="secondary" size="xs" icon={Building2}>
                  {t("overrides_count", { count: cell.org_overrides.length })}
                </ColocaButton>
              }
            >
              <div className="flex flex-col gap-1">
                {cell.org_overrides.map((override) => (
                  <ColocaButton
                    key={override.organization_id}
                    variant="ghost"
                    size="sm"
                    className="w-full justify-between gap-2"
                    onClick={() => onViewOrg(override.organization_id)}
                  >
                    <span className="truncate">
                      {orgNameById.get(override.organization_id) ?? override.organization_id}
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                      <ColocaStatusPill variant={override.enabled ? "success" : "danger"} size="sm" withDot>
                        {t(override.enabled ? "status_enabled" : "status_disabled")}
                      </ColocaStatusPill>
                      <ChevronRight className="size-3.5 text-muted-foreground" />
                    </span>
                  </ColocaButton>
                ))}
              </div>
            </ColocaPopover>
          );
        },
      });
    }

    // The switch is the whole point of this surface: enabling flips directly, disabling opens the
    // reason dialog — the backend refuses a disable without one, so the friction is real, not decorative.
    base.push({
      id: "action",
      header: "",
      headerClassName: "text-right",
      cellClassName: "text-right",
      cell: (cell) => (
        <span className="inline-flex items-center justify-end gap-2">
          {hasOwnOverride(cell, scopeOrgId) && (
            <ColocaButton variant="ghost" size="xs" icon={RotateCcw} onClick={() => onClearOverride(cell)}>
              {t("action_clear_override")}
            </ColocaButton>
          )}
          <ColocaSwitch
            checked={cell.effective_enabled}
            onCheckedChange={(checked) => onToggle(cell, checked)}
            disabled={isMutating}
            aria-label={`${formatRail(cell.rail)} · ${t(
              cell.direction === "PAYIN" ? "direction_payin" : "direction_payout",
            )}`}
          />
        </span>
      ),
    });

    return base;
  }, [t, activeLanguage, scopeOrgId, orgNameById, isMutating, onToggle, onClearOverride, onViewOrg]);

  return (
    <ColocaDataTable
      columns={columns}
      rows={cells}
      rowKey={(cell) => `${cell.rail}:${cell.direction}`}
      loading={isLoading}
      skeletonRows={skeletonRows}
      // Six columns in the global view — below this the table scrolls instead of crushing them.
      minWidth={860}
      empty={
        <ColocaEmpty
          icon={TrainTrack}
          title={t("empty_title")}
          description={t("empty_description")}
          className="border-0"
        />
      }
    />
  );
}
